import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../utils/firebaseConfig";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import useFirestoreCollection from "../utils/useFirestoreCollection";
import {
  isDateBookable,
  isTimeBookable,
  dayTimeBounds,
  firstValidSlot,
  openingLabelForDate,
} from "../utils/openingHours";
import {
  FaCheck,
  FaTimes,
  FaUsers,
  FaEnvelope,
  FaTrash,
  FaPrint,
  FaPlus,
} from "react-icons/fa";

const STATUS_LABEL = {
  pending: "Pending",
  accepted: "Accepted",
  rejected: "Rejected",
};

const isDev = process.env.NODE_ENV === "development";

// Bookable times come from the "hours" collection (see utils/openingHours.js).
const LEAD_MIN = 30; // minimum minutes ahead of now
const SLOT_MIN = 30; // time picker step
const MAX_NAME = 80;
const MAX_EMAIL = 120;
const MAX_PERSONS = 50;

// HTML escape so any user-supplied text is safe to embed in email markup.
const esc = (s) =>
  String(s == null ? "" : s).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );

// Firestore Timestamp | Date | string -> JS Date (or null if invalid).
const toJsDate = (d) => {
  if (!d) return null;
  const date = typeof d.toDate === "function" ? d.toDate() : new Date(d);
  return isNaN(date) ? null : date;
};

const formatDate = (d) => {
  const date = toJsDate(d);
  if (!date) return "";
  return date.toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// True if the two dates fall on the same calendar day (local time).
const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// Build a print-ready HTML document for today's accepted reservations and
// open it in a new window, triggering the browser's print / "Save as PDF"
// dialog. Nothing is sent anywhere — this is a local, on-demand service sheet.
const printTodaysReservations = (accepted) => {
  const now = new Date();

  const todays = accepted
    .map((r) => ({ ...r, _date: toJsDate(r.Date) }))
    .filter((r) => r._date && isSameDay(r._date, now))
    .sort((a, b) => a._date - b._date);

  const esc2 = esc; // reuse the HTML-escaper defined above
  const timeStr = (dt) =>
    dt.toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" });

  const heading = now.toLocaleDateString("el-GR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  const totalCovers = todays.reduce(
    (sum, r) => sum + (parseInt(r.Persons, 10) || 0),
    0
  );

  const rowsHtml = todays.length
    ? todays
        .map(
          (r) => `
          <tr>
            <td class="c-time">${esc2(timeStr(r._date))}</td>
            <td class="c-name">${esc2(r.Name)}</td>
            <td class="c-guests">${esc2(r.Persons)}</td>
            <td class="c-email">${esc2(r.Email)}</td>
            <td class="c-note">${esc2(r.adminNote || "")}</td>
          </tr>`
        )
        .join("")
    : `<tr><td colspan="5" class="empty">Δεν υπάρχουν εγκεκριμένες κρατήσεις για σήμερα.</td></tr>`;

  const html = `<!doctype html>
<html lang="el">
<head>
<meta charset="utf-8" />
<title>Κρατήσεις — ${esc2(heading)}</title>
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link href="https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=Caveat:wght@600;700&family=EB+Garamond:ital,wght@0,400;0,500;1,400&display=swap" rel="stylesheet" />
<style>
  /* The Old House — vintage booking sheet. Palette pulled from theme.css. */
  :root {
    --v-brown: #622f0a;
    --v-brown-dark: #3d1b04;
    --v-cream: #fad2a2;
    --v-cream-soft: #fdecd0;
    --v-olive: #6b7d3a;
    --v-terracotta: #c46d4f;
    --v-paper: #f5ebd6;
    --v-paper-soft: #fbf4e2;
    --v-ink: #2b1607;
    --v-ink-muted: #6b4d2e;
    --v-outline: rgba(98, 47, 10, 0.30);
    --v-outline-soft: rgba(98, 47, 10, 0.14);
  }
  @page { size: A4 portrait; margin: 16mm 34mm; }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    padding: 0;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  body {
    font-family: "EB Garamond", Georgia, "Times New Roman", serif;
    color: var(--v-ink);
    background: var(--v-paper-soft);
    line-height: 1.4;
  }
  /* On-screen preview mimics an A4 sheet; @page margins take over on print. */
  .sheet {
    max-width: 142mm;
    margin: 18px auto;
    padding: 26px 8px 30px;
    background: var(--v-paper-soft);
    border: 1px solid var(--v-outline);
    box-shadow: 0 0 0 4px var(--v-paper-soft), 0 0 0 5px var(--v-outline-soft);
  }
  @media print {
    .sheet { max-width: none; margin: 0; padding: 0; border: none; box-shadow: none; }
  }
  /* -------- Header -------- */
  header { text-align: center; padding-bottom: 14px; margin-bottom: 18px; }
  .rule-top {
    border: none;
    border-top: 3px double var(--v-brown);
    border-bottom: 1px solid var(--v-outline);
    height: 5px;
    margin: 0 0 16px;
  }
  .eyebrow {
    font-family: "Caveat", cursive;
    font-size: 22px;
    color: var(--v-terracotta);
    margin: 0 0 2px;
  }
  .brand {
    font-family: "DM Serif Display", Georgia, serif;
    font-size: 34px;
    letter-spacing: 0.5px;
    color: var(--v-brown);
    margin: 0;
    line-height: 1.05;
  }
  .ornament {
    color: var(--v-olive);
    font-size: 15px;
    letter-spacing: 6px;
    margin: 8px 0 6px;
  }
  .doc-date {
    font-size: 15px;
    font-style: italic;
    color: var(--v-ink-muted);
    margin: 0;
    text-transform: capitalize;
  }
  .summary {
    display: inline-block;
    margin: 12px auto 0;
    padding: 5px 16px;
    font-size: 12.5px;
    letter-spacing: 0.4px;
    color: var(--v-brown-dark);
    background: var(--v-cream-soft);
    border: 1px solid var(--v-outline-soft);
    border-radius: 999px;
  }
  /* -------- Table -------- */
  table { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
  thead th {
    text-align: left;
    padding: 9px 10px;
    font-family: "EB Garamond", serif;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: var(--v-cream-soft);
    background: var(--v-brown);
    border-bottom: 2px solid var(--v-brown-dark);
  }
  thead th:first-child { border-top-left-radius: 4px; }
  thead th:last-child { border-top-right-radius: 4px; }
  tbody td {
    padding: 9px 10px;
    border-bottom: 1px solid var(--v-outline-soft);
    vertical-align: top;
    word-break: break-word;
  }
  tbody tr:nth-child(even) { background: var(--v-cream-soft); }
  tbody tr:last-child td { border-bottom: 2px solid var(--v-outline); }
  .c-time { font-weight: 600; white-space: nowrap; width: 12%; color: var(--v-brown); }
  .c-name { width: 27%; font-weight: 500; }
  .c-guests { text-align: center; width: 10%; }
  .c-email { color: var(--v-ink-muted); width: 29%; font-size: 12px; }
  .c-note { color: var(--v-olive); font-style: italic; width: 22%; }
  .empty {
    text-align: center; padding: 28px; color: var(--v-ink-muted); font-style: italic;
    background: var(--v-cream-soft);
  }
  /* -------- Footer -------- */
  .rule-bottom {
    border: none;
    border-top: 1px solid var(--v-outline);
    margin: 20px 0 8px;
  }
  .foot-ornament { text-align: center; color: var(--v-olive); font-size: 13px; letter-spacing: 5px; margin: 4px 0 8px; }
  footer {
    text-align: center;
    font-size: 10.5px;
    font-style: italic;
    color: var(--v-ink-muted);
  }
</style>
</head>
<body>
  <div class="sheet">
    <hr class="rule-top" />
    <header>
      <p class="eyebrow">Φύλλο κρατήσεων</p>
      <h1 class="brand">The Old House</h1>
      <div class="ornament">❦ ⁂ ❦</div>
      <p class="doc-date">${esc2(heading)}</p>
      <span class="summary">${todays.length} ${
    todays.length === 1 ? "κράτηση" : "κρατήσεις"
  } &nbsp;·&nbsp; ${totalCovers} ${
    totalCovers === 1 ? "άτομο" : "άτομα"
  } &nbsp;·&nbsp; μόνο εγκεκριμένες</span>
    </header>
    <table>
      <thead>
        <tr>
          <th class="c-time">Ώρα</th><th class="c-name">Όνομα</th><th class="c-guests">Άτομα</th><th class="c-email">Email</th><th class="c-note">Σημείωση</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <hr class="rule-bottom" />
    <div class="foot-ornament">✦</div>
    <footer>Εκτυπώθηκε ${esc2(
      now.toLocaleString("el-GR")
    )} &nbsp;·&nbsp; The Old House</footer>
  </div>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (!win) {
    alert(
      "Επιτρέψτε τα αναδυόμενα παράθυρα (pop-ups) για να εκτυπώσετε τις σημερινές κρατήσεις."
    );
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  // Print once the document has rendered; guard against a double dialog if
  // both onload and the fallback timer fire.
  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    try {
      win.print();
    } catch (e) {
      /* no-op */
    }
  };
  win.onload = doPrint;
  setTimeout(doPrint, 500);
};

const renderCustomerEmail = ({ kind, reservation, adminNote }) => {
  const niceDate = esc(formatDate(reservation.Date));
  const greeting = `<p>Hi ${esc(reservation.Name)},</p>`;
  const guests = parseInt(reservation.Persons, 10) || 0;
  const guestsText = `${guests} guest${guests > 1 ? "s" : ""}`;
  const noteBlock = adminNote
    ? `<p><em>A note from us:</em><br/>${esc(adminNote).replace(
        /\n/g,
        "<br/>"
      )}</p>`
    : "";
  const signature = `<p>Warm regards,<br/>The Old House team</p>`;

  if (kind === "accepted") {
    return {
      subject: "Your reservation is confirmed",
      html: `${greeting}
        <p>Great news — your table for <strong>${guestsText}</strong> on
        <strong>${niceDate}</strong> is <strong>confirmed</strong>.</p>
        <p>We will hold your table for fifteen minutes past the
        reservation time. If anything changes on your end, just reply
        to this email.</p>
        ${noteBlock}
        <p>We look forward to seeing you!</p>
        ${signature}`,
    };
  }

  // rejected
  return {
    subject: "About your reservation request",
    html: `${greeting}
      <p>Thank you for your interest in <strong>The Old House</strong>.
      Unfortunately we are unable to confirm your request for
      <strong>${guestsText}</strong> on <strong>${niceDate}</strong>
      at this time.</p>
      ${noteBlock}
      <p>We hope to welcome you another time — please feel free to
      try a different date.</p>
      ${signature}`,
  };
};

export default function ManageReservations() {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [actionTarget, setActionTarget] = useState(null); // {reservation, kind}
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // --- Manual booking ("Προσθήκη Κράτησης") ---
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState("");
  const [addPersons, setAddPersons] = useState("");
  const [addDate, setAddDate] = useState(null);
  const [addEmail, setAddEmail] = useState("");
  const [addError, setAddError] = useState("");
  const [addSaving, setAddSaving] = useState(false);

  // The seasonal closing date, so the admin form respects the same
  // last-bookable-day limit as the public booking form.
  const { data: adminData } = useFirestoreCollection("administration");
  const closingDate = useMemo(() => {
    const cd = adminData && adminData[0] && adminData[0].closingDate;
    if (!cd) return null;
    return typeof cd.toDate === "function" ? cd.toDate() : new Date(cd);
  }, [adminData]);

  // The weekly working hours, so the picker only offers times the
  // restaurant is actually open on that particular day.
  const { data: hoursData } = useFirestoreCollection("hours");

  const { minDate, maxDate } = useMemo(() => {
    const minD = new Date();
    minD.setHours(0, 0, 0, 0);
    return {
      minDate: minD,
      maxDate: closingDate ? new Date(closingDate) : null,
    };
  }, [closingDate]);

  // Time column limits follow the day currently selected in the picker.
  const { minTime, maxTime } = useMemo(
    () => dayTimeBounds(addDate || new Date(), hoursData, SLOT_MIN),
    [addDate, hoursData]
  );

  // Grey out times outside that day's opening hours (and anything too soon).
  const filterTime = (time) => isTimeBookable(new Date(time), hoursData, LEAD_MIN);

  // Grey out closed days and days that are already over.
  const filterDate = (d) => isDateBookable(new Date(d), hoursData, LEAD_MIN);

  // Picking a day keeps the old clock time, which may now be outside that
  // day's hours — snap it to the first bookable slot instead.
  const handleAddDateChange = (d) => {
    if (!(d instanceof Date) || isNaN(d)) {
      setAddDate(d);
      return;
    }
    if (isTimeBookable(d, hoursData, LEAD_MIN)) {
      setAddDate(d);
      return;
    }
    setAddDate(firstValidSlot(d, hoursData, {
      intervalMinutes: SLOT_MIN,
      leadMinutes: LEAD_MIN,
    }) || d);
  };

  const addDayHoursLabel = addDate
    ? openingLabelForDate(addDate, hoursData)
    : "";

  // Real-time listener so admin sees new requests without refreshing.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, "reservations"),
      (snap) => {
        const rows = [];
        snap.forEach((d) => {
          const data = d.data();
          // Skip pure email payload docs (customer pending / accept / reject emails).
          if (data._emailOnly) return;
          // Skip any stray non-reservation docs.
          if (!data.Name) return;
          rows.push({ id: d.id, ...data });
        });
        // Newest pending first; accepted/rejected sorted by reservation date.
        rows.sort((a, b) => {
          const da =
            (a.Date_created && a.Date_created.toDate?.()) ||
            new Date(a.Date_created || 0);
          const db_ =
            (b.Date_created && b.Date_created.toDate?.()) ||
            new Date(b.Date_created || 0);
          return db_ - da;
        });
        setReservations(rows);
        setLoading(false);
      },
      (err) => {
        console.error("reservations listener error", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const grouped = useMemo(() => {
    const g = { pending: [], accepted: [], rejected: [] };
    for (const r of reservations) {
      const s = r.status || "pending";
      if (g[s]) g[s].push(r);
      else g.pending.push(r);
    }
    return g;
  }, [reservations]);

  const openAction = (reservation, kind) => {
    setActionTarget({ reservation, kind });
    setNoteText("");
  };

  const openAdd = () => {
    setAddName("");
    setAddPersons("");
    setAddDate(null);
    setAddEmail("");
    setAddError("");
    setShowAdd(true);
  };

  const closeAdd = () => {
    if (addSaving) return;
    setShowAdd(false);
    setAddError("");
  };

  // Create a booking by hand (e.g. taken over the phone). It goes straight
  // into "accepted" and NO email is sent to the customer.
  const submitAdd = async (e) => {
    if (e) e.preventDefault();
    setAddError("");

    const name = addName.trim();
    const email = addEmail.trim();

    if (!name) {
      setAddError("Συμπληρώστε το όνομα.");
      return;
    }
    if (name.length > MAX_NAME) {
      setAddError("Το όνομα είναι πολύ μεγάλο (έως " + MAX_NAME + " χαρακτήρες).");
      return;
    }
    const guests = parseInt(addPersons, 10);
    if (!guests || guests < 1 || guests > MAX_PERSONS) {
      setAddError("Δώστε έγκυρο αριθμό ατόμων (1 – " + MAX_PERSONS + ").");
      return;
    }
    if (!(addDate instanceof Date) || isNaN(addDate)) {
      setAddError("Επιλέξτε ημερομηνία και ώρα.");
      return;
    }
    if (addDate.getTime() < Date.now() + LEAD_MIN * 60 * 1000) {
      setAddError(
        "Επιλέξτε ώρα τουλάχιστον " + LEAD_MIN + " λεπτά από τώρα."
      );
      return;
    }
    if (!isTimeBookable(addDate, hoursData, LEAD_MIN)) {
      const label = openingLabelForDate(addDate, hoursData);
      setAddError(
        "Το εστιατόριο δεν είναι ανοικτό εκείνη την ώρα" +
          (label ? " (ωράριο ημέρας: " + label + ")." : ".")
      );
      return;
    }
    if (email) {
      if (email.length > MAX_EMAIL) {
        setAddError("Το email είναι πολύ μεγάλο.");
        return;
      }
      if (!/^\S+@\S+\.\S+$/.test(email)) {
        setAddError("Το email δεν φαίνεται σωστό.");
        return;
      }
    }

    setAddSaving(true);
    try {
      const ref = await addDoc(collection(db, "reservations"), {
        Name: name,
        Email: email,
        Date: addDate,
        Date_created: new Date(),
        Persons: guests,
        status: "accepted",
        adminNote: "",
        decidedAt: serverTimestamp(),
        createdByAdmin: true,
      });
      if (isDev) console.log("[admin] manual reservation created id=" + ref.id);
      setShowAdd(false);
      setActiveTab("accepted");
    } catch (err) {
      console.error("[admin] manual reservation failed:", err);
      setAddError("Κάτι πήγε στραβά. Δοκιμάστε ξανά.");
    } finally {
      setAddSaving(false);
    }
  };

  const deleteReservation = async (reservation) => {
    const ok = window.confirm(
      "Delete this reservation for " +
        reservation.Name +
        "?\n\nThis cannot be undone."
    );
    if (!ok) return;
    try {
      await deleteDoc(doc(db, "reservations", reservation.id));
      if (isDev) console.log("[admin] deleted reservation id=" + reservation.id);
    } catch (err) {
      console.error("[admin] delete failed:", err);
      alert("Could not delete. Please try again.");
    }
  };

  const closeAction = () => {
    if (submitting) return;
    setActionTarget(null);
    setNoteText("");
  };

  const confirmAction = async () => {
    if (!actionTarget) return;
    const { reservation, kind } = actionTarget;
    const newStatus = kind === "accept" ? "accepted" : "rejected";
    setSubmitting(true);
    try {
      if (isDev) console.log(
        `[admin] ${newStatus} reservation id=${reservation.id}` +
          (noteText.trim() ? " (with note)" : "")
      );

      // 1. Update the reservation status + admin note.
      await updateDoc(doc(db, "reservations", reservation.id), {
        status: newStatus,
        adminNote: noteText.trim(),
        decidedAt: serverTimestamp(),
      });
      if (isDev) console.log(`[admin] reservation ${reservation.id} status → ${newStatus}`);

      // 2. Send the customer email via an email-only doc in the same
      //    collection (the Firebase Trigger-Email extension picks this up).
      const { subject, html } = renderCustomerEmail({
        kind: newStatus,
        reservation,
        adminNote: noteText.trim(),
      });
      const emailRef = await addDoc(collection(db, "reservations"), {
        _emailOnly: true,
        to: [reservation.Email],
        message: { subject, html },
        relatedReservation: reservation.id,
        Date_created: new Date(),
      });
      if (isDev) console.log(
        `[admin] customer ${newStatus}-email doc written: id=${emailRef.id}`
      );

      closeAction();
    } catch (err) {
      console.error("[admin] Failed to update reservation:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: "pending", label: "Pending", count: grouped.pending.length },
    { key: "accepted", label: "Accepted", count: grouped.accepted.length },
    { key: "rejected", label: "Rejected", count: grouped.rejected.length },
  ];

  const rows = grouped[activeTab] || [];

  // Count of accepted reservations dining today, for the print button label.
  const todaysAcceptedCount = useMemo(() => {
    const now = new Date();
    return grouped.accepted.filter((r) => {
      const d = toJsDate(r.Date);
      return d && isSameDay(d, now);
    }).length;
  }, [grouped.accepted]);

  return (
    <div className="manage_reservations">
      <div className="reservations_toolbar">
        <button
          type="button"
          className="res_btn res_btn--add"
          onClick={openAdd}
          title="Καταχώρηση κράτησης με το χέρι"
        >
          <FaPlus /> Προσθήκη Κράτησης
        </button>
        <button
          type="button"
          className="res_btn res_btn--print"
          onClick={() => printTodaysReservations(grouped.accepted)}
          title="Print today's accepted reservations"
        >
          <FaPrint /> Print today's reservations
          {todaysAcceptedCount > 0 ? ` (${todaysAcceptedCount})` : ""}
        </button>
      </div>

      <div className="reservations_tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={activeTab === t.key}
            className={`reservations_tab${
              activeTab === t.key ? " is-active" : ""
            }`}
            onClick={() => setActiveTab(t.key)}
          >
            <span>{t.label}</span>
            <span className="reservations_tab__count">{t.count}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="reservations_empty">Loading reservations…</p>
      ) : rows.length === 0 ? (
        <p className="reservations_empty">No {activeTab} reservations.</p>
      ) : (
        <ul className="reservations_list">
          {rows.map((r) => (
            <li key={r.id} className="reservation_row" data-status={r.status}>
              <div className="reservation_row__main">
                <div className="reservation_row__head">
                  <h4 className="reservation_row__name">{r.Name}</h4>
                  <span
                    className="reservation_row__status"
                    data-status={r.status || "pending"}
                  >
                    {STATUS_LABEL[r.status || "pending"]}
                  </span>
                </div>
                <p className="reservation_row__date">{formatDate(r.Date)}</p>
                <div className="reservation_row__meta">
                  <span>
                    <FaUsers /> {r.Persons}
                  </span>
                  {r.Email ? (
                    <span>
                      <FaEnvelope />{" "}
                      <a href={`mailto:${r.Email}`}>{r.Email}</a>
                    </span>
                  ) : (
                    <span className="reservation_row__noemail">
                      <FaEnvelope /> —
                    </span>
                  )}
                </div>
                {r.adminNote && (
                  <p className="reservation_row__note">
                    <em>Note:</em> {r.adminNote}
                  </p>
                )}
              </div>

              <div className="reservation_row__actions">
                {(!r.status || r.status === "pending") && (
                  <>
                    <button
                      type="button"
                      className="res_btn res_btn--accept"
                      onClick={() => openAction(r, "accept")}
                    >
                      <FaCheck /> Accept
                    </button>
                    <button
                      type="button"
                      className="res_btn res_btn--reject"
                      onClick={() => openAction(r, "reject")}
                    >
                      <FaTimes /> Reject
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="res_btn res_btn--delete"
                  onClick={() => deleteReservation(r)}
                  title="Delete reservation"
                  aria-label="Delete reservation"
                >
                  <FaTrash />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <div
          className="reservation_modal__overlay"
          onClick={closeAdd}
          role="presentation"
        >
          <div
            className="reservation_modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Προσθήκη Κράτησης</h3>
            <p className="reservation_modal__subtitle">
              Η κράτηση θα καταχωρηθεί απευθείας στις εγκεκριμένες. Δεν
              στέλνεται email στον πελάτη.
            </p>

            <form onSubmit={submitAdd}>
              <label className="reservation_modal__label">
                Όνομα
                <input
                  type="text"
                  value={addName}
                  onChange={(e) => setAddName(e.target.value)}
                  maxLength={MAX_NAME}
                  placeholder="π.χ. Γιώργος Παπαδόπουλος"
                  autoFocus
                />
              </label>

              <label className="reservation_modal__label">
                Άτομα
                <input
                  type="number"
                  value={addPersons}
                  onChange={(e) => setAddPersons(e.target.value)}
                  min="1"
                  max={MAX_PERSONS}
                  placeholder="π.χ. 4"
                />
              </label>

              <div className="reservation_modal__label">
                Ημερομηνία και ώρα
                <DatePicker
                  selected={addDate}
                  onChange={handleAddDateChange}
                  showTimeSelect
                  dateFormat="d MMMM, yyyy HH:mm"
                  timeFormat="HH:mm"
                  minDate={minDate}
                  maxDate={maxDate || undefined}
                  minTime={minTime}
                  maxTime={maxTime}
                  filterTime={filterTime}
                  filterDate={filterDate}
                  timeIntervals={SLOT_MIN}
                  shouldCloseOnSelect={false}
                  placeholderText="Επιλέξτε ημερομηνία και ώρα"
                  autoComplete="off"
                  withPortal
                />
                {addDayHoursLabel && (
                  <span className="reservation_modal__hint">
                    Ωράριο ημέρας: {addDayHoursLabel}
                  </span>
                )}
              </div>

              <label className="reservation_modal__label">
                Email (προαιρετικό)
                <input
                  type="email"
                  value={addEmail}
                  onChange={(e) => setAddEmail(e.target.value)}
                  maxLength={MAX_EMAIL}
                  placeholder="you@example.com"
                />
              </label>

              {addError && (
                <p className="reservation_modal__error" role="alert">
                  {addError}
                </p>
              )}

              <div className="reservation_modal__actions">
                <button
                  type="button"
                  className="res_btn res_btn--ghost"
                  onClick={closeAdd}
                  disabled={addSaving}
                >
                  Ακύρωση
                </button>
                <button
                  type="submit"
                  className="res_btn res_btn--accept"
                  disabled={addSaving}
                >
                  {addSaving ? "Αποθήκευση…" : "Αποθήκευση κράτησης"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {actionTarget && (
        <div
          className="reservation_modal__overlay"
          onClick={closeAction}
          role="presentation"
        >
          <div
            className="reservation_modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>
              {actionTarget.kind === "accept"
                ? "Accept reservation"
                : "Reject reservation"}
            </h3>
            <p className="reservation_modal__subtitle">
              <strong>{actionTarget.reservation.Name}</strong> —{" "}
              {formatDate(actionTarget.reservation.Date)} ·{" "}
              {actionTarget.reservation.Persons} guests
            </p>
            <label className="reservation_modal__label">
              Optional note for the customer
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                rows={4}
                placeholder={
                  actionTarget.kind === "accept"
                    ? "e.g. We'll prepare the corner table for you."
                    : "e.g. We're fully booked that evening, but Sunday is available."
                }
              />
            </label>
            <div className="reservation_modal__actions">
              <button
                type="button"
                className="res_btn res_btn--ghost"
                onClick={closeAction}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`res_btn ${
                  actionTarget.kind === "accept"
                    ? "res_btn--accept"
                    : "res_btn--reject"
                }`}
                onClick={confirmAction}
                disabled={submitting}
              >
                {submitting
                  ? "Sending…"
                  : actionTarget.kind === "accept"
                  ? "Confirm & email customer"
                  : "Reject & email customer"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
